import React, { useMemo } from "react";
import { UserLoggedInNavigatorScreenProps } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { Box } from "arkh";
import { Pager } from "../../../components/Pager";
import { useLayout } from "../../TodoCreate/useLayout";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { StepController } from "../../../components/StepController";
import { OrgSpaceCreatePageSize } from "./OrgSpaceCreatePageSize";
import { OrgSpaceCreatePageName } from "./OrgSpaceCreatePageName";
import { OrgSpaceCreatePageSuccess } from "./OrgSpaceCreatePageSuccess";
import { graphql, useMutation } from "react-relay";
import { OrgSpaceCreateMutation } from "./__generated__/OrgSpaceCreateMutation.graphql";
import { useAlert } from "../../../common/Alert";
import { useIntl } from "react-intl";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import {
  OrgSpaceCreatePageIndexType,
  OrgSpaceCreateHeightType,
} from "./OrgSpaceCreatePageIndexType";
import { useNavigationHeader } from "../../../common/navigationHeaderOptions";

export const OrgSpaceCreate = withWebModalStack(
  (props: UserLoggedInNavigatorScreenProps<"OrgSpaceCreate">) => {
    const [layout, onLayout] = useLayout();

    const [pageIndex, setPageIndex] =
      React.useState<OrgSpaceCreatePageIndexType>({ index: 0 });
    const [name, setName] = React.useState("");
    const [height, setHeight] = React.useState<OrgSpaceCreateHeightType>(1);
    const pages = useMemo(() => {
      return [
        <OrgSpaceCreatePageSize
          key="page-size"
          height={height}
          setHeight={setHeight}
        />,
        <OrgSpaceCreatePageName
          key="page-name"
          name={name}
          setName={setName}
        />,
        <OrgSpaceCreatePageSuccess key="page-success" name={name} />,
      ];
    }, [height, name]);

    const [create, createLoading] = useMutation<OrgSpaceCreateMutation>(graphql`
      mutation OrgSpaceCreateMutation(
        $workspaceId: ID!
        $name: String!
        $size: Int!
      ) {
        workspace(id: $workspaceId) {
          createStorecastSpace(height: $size, name: $name) {
            storecastSpace {
              id
            }
            workspace {
              ...OrgSpacesComponent_workspace
            }
          }
        }
      }
    `);
    const alert = useAlert();
    const intl = useIntl();
    const intl_tokens = useIntlTokens();

    const onCreate = React.useCallback(() => {
      if (height === null) {
        return;
      }
      create({
        variables: {
          workspaceId: props.route.params.workspaceId,
          name,
          size: height,
        },
        onError: () => {
          alert.alert(
            intl.formatMessage({
              defaultMessage: "Something went wrong",
              description: "OrgSpaceCreate mutation error alert title",
            }),
            intl.formatMessage({
              defaultMessage: "The space is not created. Please try again.",
              description: "OrgSpaceCreate mutation error alert message",
            }),
            [
              {
                style: "default",
                isPreferred: true,
                text: intl_tokens.OK,
              },
            ],
            {}
          );
        },
        onCompleted: (data) => {
          setPageIndex({
            index: 2,
            id: data.workspace.createStorecastSpace.storecastSpace.id,
          });
        },
      });
    }, [
      alert,
      create,
      height,
      intl,
      intl_tokens.OK,
      name,
      props.route.params.workspaceId,
    ]);

    const disabled = React.useMemo(() => {
      switch (pageIndex.index) {
        case 0:
          return height === null;
        case 1:
          return name === "";
        default:
          return false;
      }
    }, [height, name, pageIndex]);

    useNavigationHeader({
      type: "CLOSE",
      onClose: () => props.navigation.goBack(),
      title: "",
    });

    return (
      <Box style={{ flex: 1 }} onLayout={onLayout} color="background">
        <Box style={{ flex: 1 }}>
          {layout.width > 1 ? (
            <Pager
              pageIndex={pageIndex.index}
              pages={pages}
              width={layout.width}
              key={layout.width + ""}
            />
          ) : null}
        </Box>
        <StepController
          color="surface"
          space="medium"
          disabledKeyboardHandling={false}
          nextLoading={pageIndex.index === 1 ? createLoading : undefined}
          onNext={() => {
            if (pageIndex.index === 2) {
              props.navigation.navigateDeprecated("OrgHome", {
                screen: "OrgSpaces",
                params: {
                  newlyCreatedSpaceId: pageIndex.id,
                },
              });
              props.navigation.goBack();
            } else if (pageIndex.index === 1) {
              onCreate();
            } else {
              setPageIndex({ index: 1 });
            }
          }}
          onPrev={
            pageIndex.index === 1
              ? () => {
                  setPageIndex({ index: 0 });
                }
              : undefined
          }
          create={pageIndex.index === 1}
          nextTitle={pageIndex.index === 2 ? intl_tokens.Done : undefined}
          disabled={disabled}
        />
      </Box>
    );
  }
);
